import React from "react";
import { ArrowRight, ArrowDownRight } from "lucide-react";
import { LogoMark } from "./Logo";
import Counter from "./Counter";

const HERO_IMG =
  "https://images.unsplash.com/photo-1762536859942-8076505f7c62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBoaWdocmlzZSUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3Nzg2NTgyNTB8MA&ixlib=rb-4.1.0&q=85";

const stats = [
  { label: "Years of Practice", value: 50, suffix: "+" },
  { label: "Projects Delivered", value: 200, suffix: "+" },
  { label: "Industrial Clients", value: 40, suffix: "+" },
  { label: "Cities Served", value: 12, suffix: "" },
];

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden grain">
      {/* Background image with slow zoom */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="High-rise construction skyline"
          className="w-full h-full object-cover animate-slow-zoom"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/95 via-ink-900/80 to-ink-900" />
        <div className="absolute inset-0 grid-bg opacity-50" />
      </div>

      {/* Watermark logo */}
      <div className="absolute right-[-120px] bottom-[-80px] opacity-[0.06] pointer-events-none hidden md:block">
        <LogoMark className="h-[520px] w-[520px]" variant="mono" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-40 lg:pt-44 pb-20">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-9">
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-8 animate-fade-in" data-testid="hero-eyebrow">
              <span className="h-px w-10 bg-gold" />
              <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-gold">
                Estd. 1972 · Mumbai · Maharashtra
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display font-medium text-balance text-white leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[88px] xl:text-[104px] tracking-tight"
            >
              <span className="block">Fifty years of</span>
              <span className="block italic text-gold">trusted engineering</span>
              <span className="block">excellence.</span>
            </h1>

            <p
              data-testid="hero-subhead"
              className="mt-10 max-w-2xl text-base md:text-lg text-steel-light leading-relaxed"
            >
              Sanitary Engineers and Licensed Plumbers delivering precision plumbing
              infrastructure across high-rise residential, commercial, industrial and
              Mivan-built developments throughout Maharashtra.
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <a
                href="#projects"
                data-testid="hero-cta-projects"
                className="group inline-flex items-center gap-3 bg-gold text-ink-900 px-7 py-4 text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-gold-light transition-all duration-300 shadow-[0_0_24px_rgba(197,160,89,0.25)]"
              >
                View Projects
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="group inline-flex items-center gap-3 border border-white/20 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em] font-semibold hover:border-gold hover:text-gold transition-all duration-300"
              >
                Contact Us
                <ArrowDownRight size={16} className="transition-transform duration-300 group-hover:translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-24 lg:mt-32 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
          {stats.map((s, i) => (
            <div
              key={s.label}
              data-testid={`hero-stat-${i}`}
              className="bg-ink-900/80 backdrop-blur-sm px-6 py-8 md:px-8 md:py-10"
            >
              <div className="font-display text-4xl md:text-5xl text-white tracking-tight">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-steel-light">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom hairline */}
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
    </section>
  );
};

export default Hero;
